import { useState } from 'react';
import { type DropzoneOptions, type FileRejection, ErrorCode, useDropzone } from 'react-dropzone';

// react-dropzone silently drops anything `accept` refuses. Every dropzone in the app
// narrows the file types it takes, so without this the user drags — or since 19.2.0
// pastes — a file and nothing whatsoever happens. The suffixes match the translation
// keys under Generic.FileRejection.
const rejectionCodes: Record<string, string> = {
    [ErrorCode.FileInvalidType]: 'FileInvalidType',
    [ErrorCode.FileTooLarge]: 'FileTooLarge',
    [ErrorCode.FileTooSmall]: 'FileTooSmall',
    [ErrorCode.TooManyFiles]: 'TooManyFiles'
};

export const toRejectionCode = (fileRejections: readonly FileRejection[]): string | undefined => {
    const code = fileRejections[0]?.errors[0]?.code;
    if (!code) return undefined;

    // A custom validator may return a code of its own, which has no key of its own either.
    return rejectionCodes[code] ?? 'Unknown';
};

/**
 * useDropzone, but a refused file says so. Returns `rejectionCode`, a suffix for
 * `<ErrorMessage errorSubject="Generic.FileRejection." />`, or undefined when the last
 * drop was clean. A successful drop clears it.
 */
export const useFileDropzone = ({ onDrop, ...options }: DropzoneOptions) => {
    const [rejectionCode, setRejectionCode] = useState<string>();

    const dropzone = useDropzone({
        ...options,
        onDrop: (acceptedFiles, fileRejections, event) => {
            setRejectionCode(toRejectionCode(fileRejections));
            onDrop?.(acceptedFiles, fileRejections, event);
        }
    });

    return { ...dropzone, rejectionCode };
};
